// Stok ve depo: açılış stoğu, eksi stok, kargo ölçüleri ve tartı (birimler/ambalajlar ayrı sekmede). Yalnız ürün tipinde görünür.
import React from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Chip, FormControlLabel, InputAdornment, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { useTranslation } from 'react-i18next';
import type { StockCardForm } from '../useStockCardForm';
import { AddLink, GroupLabel, SectionTitle, toNumberOrEmpty } from './fields';

export const StockSection: React.FC<{ f: StockCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');
  const qty = f.quantity === '' ? 0 : Number(f.quantity);
  const noQty = qty === 0;

  return (
    <Box>
      <SectionTitle
        title={t('stock.addModal.layout.sections.stock', 'Stok ve depo')}
        hint={f.isEditMode
          ? t('stock.addModal.fields.quantityEditableFromTable')
          : t('stock.addModal.layout.openingHint', 'Açılış stoğu isteğe bağlı. Miktar girerseniz depo seçin.')}
      />

      <GroupLabel>{t('stock.addModal.layout.openingStock', 'Açılış stoğu')}</GroupLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
        <Tooltip title={f.isEditMode ? t('stock.addModal.fields.quantityEditHint') : ''} arrow placement="top">
          <TextField
            id="stock-card-quantity"
            fullWidth
            size="small"
            type="number"
            label={t('stock.addModal.fields.quantity')}
            value={f.quantity}
            onChange={(e) => f.setQuantity(toNumberOrEmpty(e.target.value))}
            disabled={f.isEditMode}
            inputProps={{ min: 0 }}
            InputProps={{ endAdornment: <InputAdornment position="end">{f.getUnitLabel(f.unit)}</InputAdornment> }}
          />
        </Tooltip>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', minWidth: 0 }}>
          <Autocomplete
            fullWidth
            size="small"
            id="stock-card-warehouse"
            options={f.warehouses}
            getOptionLabel={(option) => option.name}
            value={f.selectedWarehouse}
            onChange={(_, value) => f.handleWarehouseChange(value)}
            disabled={noQty}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={noQty ? t('stock.addModal.fields.warehouseNoQuantity') : t('stock.addModal.fields.warehouse')}
                error={qty > 0 && !f.warehouseId}
                helperText={noQty ? t('stock.addModal.fields.warehouseNoQuantityHint') : t('stock.addModal.fields.warehouseHint')}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <WarehouseIcon fontSize="small" sx={{ mr: 1, color: option.isDefault ? 'primary.main' : 'action.active' }} />
                <Typography variant="body2" sx={{ fontWeight: option.isDefault ? 700 : 400 }}>
                  {option.name}{option.isDefault && ` (${t('stock.quickAdd.default')})`}
                </Typography>
                <Chip
                  label={option.warehouseType === 'Smart' ? t('stock.addModal.fields.smartWarehouse') : t('stock.addModal.fields.normalWarehouse')}
                  size="small"
                  variant="outlined"
                  color={option.warehouseType === 'Smart' ? 'info' : 'default'}
                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                />
              </li>
            )}
          />
          <Tooltip title={t('stock.addModal.layout.newWarehouse', 'Yeni depo')}>
            <Button
              variant="outlined"
              size="small"
              onClick={f.handleOpenWarehouseModal}
              aria-label={t('stock.addModal.layout.newWarehouse', 'Yeni depo')}
              sx={{ minWidth: 40, height: 40, p: 0 }}
            >
              <AddIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Box>

        {f.isSmartWh && (
          <Autocomplete
            fullWidth
            size="small"
            id="stock-card-location"
            options={f.locationOpts}
            getOptionLabel={(option) => option.code || option.name || ''}
            value={f.locationOpts.find(l => l.id === f.selectedLocationId) || null}
            onChange={(_, value) => f.setSelectedLocationId(value ? value.id : null)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2">{option.code}</Typography>
                  {option.name && option.name !== option.code && (
                    <Typography variant="caption" color="text.secondary">{option.name}</Typography>
                  )}
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('stock.addModal.fields.locationRequired')}
                error={!f.selectedLocationId && qty > 0}
                helperText={f.locationEmptyForSmart
                  ? t('stock.addModal.fields.locationNotFoundHint')
                  : (!f.selectedLocationId && qty > 0 ? t('stock.addModal.fields.locationRequiredHint') : '')}
              />
            )}
          />
        )}
      </Box>

      <FormControlLabel
        sx={{ mt: 1 }}
        control={<Checkbox checked={f.canBeNegative} onChange={(e) => f.setCanBeNegative(e.target.checked)} />}
        label={t('stock.quickAdd.fields.canBeNegative')}
      />

      <GroupLabel>{t('stock.addModal.layout.shipping', 'Kargo ve ölçüler')}</GroupLabel>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5, alignItems: 'start' }}>
        <TextField
          id="stock-card-desi"
          fullWidth
          size="small"
          type="number"
          label="Desi"
          value={f.variantDesi}
          onChange={(e) => f.setVariantDesi(toNumberOrEmpty(e.target.value))}
          inputProps={{ min: 0, step: '0.001' }}
        />
        {f.showDesiCalculator ? (
          <>
            <TextField id="stock-card-length" fullWidth size="small" type="number"
              label={t('stock.addModal.layout.length', 'Boy')} value={f.variantLength}
              onChange={(e) => f.setVariantLength(toNumberOrEmpty(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} />
            <TextField id="stock-card-width" fullWidth size="small" type="number"
              label={t('stock.addModal.layout.width', 'En')} value={f.variantWidth}
              onChange={(e) => f.setVariantWidth(toNumberOrEmpty(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} />
            <TextField id="stock-card-height" fullWidth size="small" type="number"
              label={t('stock.addModal.layout.height', 'Yükseklik')} value={f.variantHeight}
              onChange={(e) => f.setVariantHeight(toNumberOrEmpty(e.target.value))}
              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }} />
          </>
        ) : null}
      </Box>
      {f.showDesiCalculator ? (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, flexWrap: 'wrap' }}>
          <Button size="small" variant="contained" onClick={f.handleCalculateDesi}>
            {t('stock.addModal.layout.calcDesi', 'Ölçülerden desi hesapla')}
          </Button>
          <Typography variant="caption" color="text.secondary">(Boy × En × Yükseklik) / 3000</Typography>
        </Box>
      ) : (
        <AddLink label={t('stock.addModal.layout.openDesiCalc', 'Ölçülerden hesapla')} onClick={f.handleToggleDesiCalculator} />
      )}

      <GroupLabel>{t('stock.addModal.layout.scale', 'Tartı')}</GroupLabel>
      <FormControlLabel
        control={<Checkbox checked={f.isScale} onChange={(e) => f.setIsScale(e.target.checked)} />}
        label={t('stock.addModal.layout.isScale', 'Tartılı ürün (terazide satılır)')}
      />
      {f.isScale && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, mt: 0.5 }}>
          <TextField
            id="stock-card-scale-code"
            fullWidth
            size="small"
            label={t('stock.addModal.layout.scaleCode', 'Tartı kodu')}
            value={f.scaleCode}
            onChange={(e) => f.setScaleCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="00001"
            helperText={f.scaleCodeHelperText}
            inputProps={{ inputMode: 'numeric', maxLength: 5 }}
          />
          {!f.lotTrackingEnabled && (
            <TextField
              id="stock-card-shelf-life"
              fullWidth
              size="small"
              label={t('stock.addModal.layout.shelfLife', 'Raf ömrü (gün)')}
              value={f.shelfLifeDays}
              onChange={(e) => f.setShelfLifeDays(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="3"
              helperText={f.shelfLifeDaysHelperText}
              inputProps={{ inputMode: 'numeric', maxLength: 4 }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};
